import { useState, type FormEvent } from "react";
import { mailingListConsent } from "../../../shared/src/email-subscribers-runtime";
import { Checkbox, Input } from "./ui-kit/forms";
import "../../src/ui/tokens.css";
import "../../src/ui/forms.css";
import "../../src/ui/newsletter.css";

/** The same mailing-list form in authored pages, newsletter sections and editor previews. */
export function Newsletter({
  id,
  creatorName,
  buttonLabel = "Subscribe",
  askName = false,
  preview = false,
  pagePath = "/",
}: {
  id: string;
  creatorName: string;
  buttonLabel?: string;
  askName?: boolean;
  preview?: boolean;
  pagePath?: string;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview || status === "sending") return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    setStatus("sending");
    try {
      const response = await fetch("/api/site/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: fields.get("email"),
          name: fields.get("name") ?? "",
          consent: fields.get("consent") === "on",
          website: fields.get("website"),
          pagePath: location.pathname,
        }),
      });
      const result = (await response.json()) as { status?: string };
      if (!response.ok || result.status !== "subscribed") throw new Error("Signup failed");
      form.reset();
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }
  return (
    <form
      action="/api/site/subscribe"
      method="post"
      className="ui-newsletter"
      data-ui="newsletter"
      onSubmit={(event) => void submit(event)}
    >
      <fieldset disabled={preview || status === "sending" || status === "sent"}>
        {askName && (
          <Input
            id={`${id}-name`}
            name="name"
            label="Name (optional)"
            autoComplete="name"
            maxLength={100}
          />
        )}
        <Input
          id={`${id}-email`}
          name="email"
          label="Email address"
          type="email"
          autoComplete="email"
          required
          maxLength={320}
          placeholder="you@example.com"
        />
        <div hidden aria-hidden="true">
          <label>
            Website
            <input name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <input type="hidden" name="pagePath" value={pagePath} />
        <Checkbox
          id={`${id}-consent`}
          name="consent"
          required
          label={mailingListConsent(creatorName)}
        />
        <button className="button button-primary" type="submit">
          {status === "sending" ? "Subscribing…" : buttonLabel}
        </button>
      </fieldset>
      <p role="status" aria-live="polite">
        {status === "sent"
          ? "Thank you. You’re on the list."
          : status === "failed"
            ? "We couldn’t save your signup. Please try again."
            : preview
              ? "Preview: signups aren’t saved here."
              : "Your email joins this mailing list. No member account is created."}
      </p>
    </form>
  );
}
